// Pure helpers for the member history view: days, route geometry and the
// stops/trips timeline.
import type { Schemas } from "@10twenty/api";

export { distanceText, speedText } from "../units";

export type HistoryPoint = Schemas["HistoryPoint"];
export type StopSegment = Schemas["StopSegment"];
export type TripSegment = Schemas["TripSegment"];
/** A GeoJSON position: `[lon, lat]`. */
export type Position = [number, number];

/** Unaccounted time between segments longer than this shows as "No location data" (as on Android). */
export const NO_DATA_MS = 10 * 60_000;
/** Stops are drawn at least this large, so a tight cluster is still visible. */
export const MIN_STOP_RADIUS_M = 20;

const EARTH_RADIUS_M = 6_371_008.8;
const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

// ------------------------------------------------------------------ days

/** The local calendar day of `date` as `YYYY-MM-DD`. */
export function dayKey(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${String(date.getFullYear()).padStart(4, "0")}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * The start of the local day `offsetDays` after `key` (midnight, or the first
 * existing time where a DST change skips midnight), or null if `key` is not a
 * real `YYYY-MM-DD` date.
 */
function dayStart(key: string, offsetDays = 0): Date | null {
  const match = DAY_PATTERN.exec(key);
  if (!match) return null;
  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const at = (days: number) => {
    // From calendar fields each time: adding days to a normalized 01:00 would keep the hour.
    // setFullYear keeps two-digit years out of 19xx.
    const date = new Date(2000, 0, 1);
    date.setFullYear(y, m - 1, d + days);
    return date;
  };
  const date = at(0);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return offsetDays === 0 ? date : at(offsetDays);
}

export function isDayKey(key: string): boolean {
  return dayStart(key) !== null;
}

/** The day `delta` days after `key` (which must be valid). */
export function shiftDay(key: string, delta: number): string {
  const date = dayStart(key, delta);
  if (!date) throw new RangeError(`Invalid day ${key}`);
  return dayKey(date);
}

/** The day to show for a `?day=` value: today unless it names a valid day up to today. */
export function resolveDay(param: string | null, nowMs: number): string {
  const today = dayKey(new Date(nowMs));
  return param !== null && isDayKey(param) && param <= today ? param : today;
}

/** RFC 3339 UTC instant with whole seconds (as the server and the Android client use). */
function instant(date: Date): string {
  return date.toISOString().replace(/\.\d{3}Z$/, "Z");
}

/**
 * The half-open range `[local midnight, next local midnight)` of `key`, so
 * days around a DST change are 23 or 25 hours long.
 */
export function dayRange(key: string): { from: string; to: string } {
  const from = dayStart(key);
  const to = dayStart(key, 1);
  if (!from || !to) throw new RangeError(`Invalid day ${key}`);
  return { from: instant(from), to: instant(to) };
}

/** "Today", "Yesterday" or the date, for the day bar. */
export function dayTitle(key: string, nowMs: number, locale?: string): string {
  const today = dayKey(new Date(nowMs));
  if (key === today) return "Today";
  if (key === shiftDay(today, -1)) return "Yesterday";
  const date = dayStart(key);
  if (!date) return key;
  return date.toLocaleDateString(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(key.slice(0, 4) === today.slice(0, 4) ? {} : { year: "numeric" }),
  });
}

// ------------------------------------------------------------------ text

export function durationText(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  if (minutes < 1) return "<1 min";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

export function timeText(ms: number, locale?: string): string {
  return new Date(ms).toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
}

// -------------------------------------------------------------- timeline

interface EntryBase {
  /** Stable across refreshes: kind and start time. */
  key: string;
  start: number;
  end: number;
}

export type TimelineEntry =
  | (EntryBase & { kind: "stop"; stop: StopSegment })
  | (EntryBase & { kind: "trip"; trip: TripSegment })
  | (EntryBase & { kind: "no-data" })
  | (EntryBase & { kind: "place"; event: Schemas["PlaceEvent"] })
  | (EntryBase & { kind: "check-in"; checkIn: Schemas["CheckIn"] });

/** The selection key of a segment: entry kind and start time, as on Android. */
export function entryKey(kind: TimelineEntry["kind"], startMs: number): string {
  return `${kind}:${startMs}`;
}

/**
 * Stops and trips, oldest first, with "no location data" entries where more
 * than `NO_DATA_MS` between consecutive segments is unaccounted for. Place
 * arrivals and departures and check-ins are instants placed by time, before
 * an entry that starts at the same moment.
 */
export function timeline(
  segments: readonly Schemas["HistorySegment"][],
  placeEvents: readonly Schemas["PlaceEvent"][] = [],
  checkIns: readonly Schemas["CheckIn"][] = [],
): TimelineEntry[] {
  const entries: TimelineEntry[] = [];
  let previousEnd: number | null = null;
  for (const segment of segments) {
    const start = Date.parse(segment.start);
    const end = Date.parse(segment.end);
    if (previousEnd !== null && start - previousEnd > NO_DATA_MS) {
      entries.push({ kind: "no-data", key: entryKey("no-data", previousEnd), start: previousEnd, end: start });
    }
    entries.push(
      segment.kind === "stop"
        ? { kind: "stop", key: entryKey("stop", start), start, end, stop: segment }
        : { kind: "trip", key: entryKey("trip", start), start, end, trip: segment },
    );
    previousEnd = previousEnd === null ? end : Math.max(previousEnd, end);
  }
  if (!placeEvents.length && !checkIns.length) return entries;
  const events = [
    ...placeEvents.map((event): TimelineEntry => {
      const at = Date.parse(event.ts);
      return { kind: "place", key: `place:${event.id}`, start: at, end: at, event };
    }),
    ...checkIns.map((checkIn): TimelineEntry => {
      const at = Date.parse(checkIn.ts);
      return { kind: "check-in", key: `check-in:${checkIn.id}`, start: at, end: at, checkIn };
    }),
  ].sort((a, b) => a.start - b.start);
  const merged: TimelineEntry[] = [];
  let next = 0;
  for (const entry of entries) {
    while (next < events.length && events[next]!.start <= entry.start) merged.push(events[next++]!);
    merged.push(entry);
  }
  return merged.concat(events.slice(next));
}

export function totalDistance(segments: readonly Schemas["HistorySegment"][]): number {
  return segments.reduce((sum, s) => sum + (s.kind === "trip" ? s.distance_m : 0), 0);
}

/** How far a trip may start from a weekly-summary drive's start and still be that drive. */
export const TRIP_MATCH_MS = 10 * 60_000;

/**
 * The key of the trip a weekly-summary drive starting at `startMs` opens: the
 * trip containing that instant, else the one starting nearest to it (within
 * `TRIP_MATCH_MS`), since the day's history may segment it slightly differently.
 */
export function tripAt(entries: readonly TimelineEntry[], startMs: number): string | null {
  let best: { key: string; distance: number } | null = null;
  for (const entry of entries) {
    if (entry.kind !== "trip") continue;
    const distance = entry.start <= startMs && startMs < entry.end ? 0 : Math.abs(entry.start - startMs);
    if (distance <= TRIP_MATCH_MS && (best === null || distance < best.distance)) best = { key: entry.key, distance };
  }
  return best?.key ?? null;
}

// -------------------------------------------------------------- geometry

/**
 * Every point as a position, with longitudes unwrapped so that consecutive
 * points never differ by more than 180° (a route across the antimeridian goes
 * the short way; MapLibre draws longitudes beyond ±180 on the next world copy).
 */
export function routePositions(points: readonly HistoryPoint[]): Position[] {
  const positions: Position[] = [];
  let previous: number | null = null;
  for (const point of points) {
    const lon: number = previous === null ? point.lon : wrapNear(point.lon, previous);
    positions.push([lon, point.lat]);
    previous = lon;
  }
  return positions;
}

/** `lon` shifted by whole turns to lie within 180° of `reference`. */
export function wrapNear(lon: number, reference: number): number {
  return lon + 360 * Math.round((reference - lon) / 360);
}

/**
 * The route as lines split wherever `gap_before` is set (clients must not
 * join across a gap); a run of a single fix becomes a dot.
 */
export function routeGeometry(
  points: readonly HistoryPoint[],
  positions: readonly Position[] = routePositions(points),
): { lines: Position[][]; dots: Position[] } {
  const lines: Position[][] = [];
  const dots: Position[] = [];
  let run: Position[] = [];
  const flush = () => {
    if (run.length === 1) dots.push(run[0]!);
    else if (run.length > 1) lines.push(run);
    run = [];
  };
  points.forEach((point, i) => {
    if (i > 0 && point.gap_before) flush();
    run.push(positions[i]!);
  });
  flush();
  return { lines, dots };
}

/** The trip's slice of the (simplified) positions, `first_point..last_point` inclusive. */
export function tripPositions(positions: readonly Position[], trip: TripSegment): Position[] {
  return positions.slice(trip.first_point, trip.last_point + 1);
}

/** The stop's centre, on the same world copy as the route around it. */
export function stopCenter(
  stop: StopSegment,
  points: readonly HistoryPoint[],
  positions: readonly Position[],
): Position {
  const start = Date.parse(stop.start);
  const index = points.findIndex((p) => Date.parse(p.ts) >= start);
  const reference = positions[index === -1 ? positions.length - 1 : index];
  return [reference ? wrapNear(stop.lon, reference[0]) : stop.lon, stop.lat];
}

/** A closed ring approximating a circle of `radiusM` metres (local equirectangular). */
export function circleRing(lat: number, lon: number, radiusM: number, sides = 32): Position[] {
  const dLat = (radiusM / EARTH_RADIUS_M) * (180 / Math.PI);
  const dLon = dLat / Math.max(Math.cos((lat * Math.PI) / 180), 1e-6);
  const ring: Position[] = [];
  for (let i = 0; i < sides; i++) {
    const angle = (2 * Math.PI * i) / sides;
    ring.push([lon + dLon * Math.cos(angle), lat + dLat * Math.sin(angle)]);
  }
  ring.push(ring[0]!);
  return ring;
}

/** `[[west, south], [east, north]]` of `positions`, or null if there are none. */
export function boundsOf(positions: readonly Position[]): [Position, Position] | null {
  if (positions.length === 0) return null;
  let [west, south] = positions[0]!;
  let [east, north] = positions[0]!;
  for (const [lon, lat] of positions) {
    west = Math.min(west, lon);
    east = Math.max(east, lon);
    south = Math.min(south, lat);
    north = Math.max(north, lat);
  }
  return [
    [west, south],
    [east, north],
  ];
}

export function historyPath(circleId: string, userId: string, day?: string): string {
  const path = `/circles/${encodeURIComponent(circleId)}/members/${encodeURIComponent(userId)}/history`;
  return day === undefined ? path : `${path}?day=${day}`;
}
